import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { eventsApi } from 'src/Services/API';
import { EventDto, UpdateEventDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { ApiError } from 'src/types/api-error.types';

type UpdateEventParams = {
  eventId: string;
  data: Omit<UpdateEventDto, 'timezone'>;
};

export const useUpdateEvent = (
  options?: UseMutationOptions<EventDto, ApiError, UpdateEventParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<EventDto, ApiError, UpdateEventParams>(
    ['update-event', assetId],
    async ({ eventId, data }) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await eventsApi.eventsControllerUpdate(assetId, eventId, {
        ...data,
      });

      return response.data;
    },
    {
      ...options,
      onSuccess: (data: EventDto, variables: UpdateEventParams, context: unknown) => {
        queryClient.invalidateQueries({ queryKey: ['fetch-events'] });
        queryClient.invalidateQueries({ queryKey: ['fetch-event', data.id] });
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
        trackEvent('Planning', 'update', variables.data.name);
      },
    }
  );
};
